/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  DiscountStatusEnum,
  DocusignConfigSchema,
  OfferingStatusEnum,
  PaymentMethodReqSchemaUI,
  ProductStatusEnum,
  SharedInvoicePaymentSchemaUI,
  TenantStatusEnum,
  UsageTypeStatusEnum,
} from '@monetize/types/app';
import {
  DataTablePagerSchema,
  DEFAULT_PAGER,
  FilterType,
  FilterTypeOperator,
  PartialRecord,
  TDataTablePager,
} from '@monetize/types/common';
import { z } from 'zod';
import {
  HIDE_TEST_ACCOUNT_FALSE_VALUE,
  HIDE_TEST_ACCOUNT_TRUE_VALUE,
  TEST_ACCOUNT_FILTER_KEY,
} from '../constants/testAccountFilter.constants';

function hideTestAccountDefaultFilter(): FilterType {
  return {
    key: TEST_ACCOUNT_FILTER_KEY,
    operator: FilterTypeOperator.TOGGLE,
    options: {
      falseValue: HIDE_TEST_ACCOUNT_FALSE_VALUE,
      trueValue: HIDE_TEST_ACCOUNT_TRUE_VALUE,
    },
    value: HIDE_TEST_ACCOUNT_TRUE_VALUE,
  };
}

export enum PersistKeysEnum {
  USER_ID = 'persist:userId',
  CURRENT_TENANT = 'persist:currentTenant',
  ACCESS_TOKEN = 'persist:accessToken',
  PAYMENT_METHOD = 'persist:temp:paymentMethod',
  DOCUSIGN = 'persist:temp:docusign',
  REDIRECT_INFO = 'persist:temp:redirectInfo',
  QUOTE_PREFERENCES = 'persist:quotes',
  TABLE_FILTERS = 'persist:tableFilters',
  TABLE_PAGERS = 'persist:tablePagers',
  TABLE_SEARCH_TERMS = 'persist:tableSearchTerms',
  SHARED_INVOICE_KEY = 'persist:temp:sharedInvoice',
  SHARED_QUOTE_PAYMENT_METHOD_STRIPE_CALLBACK_KEY = 'persist:temp:sharedQuotePaymentMethod',
  SHARED_ACCOUNT_PAYMENT_METHOD_STRIPE_CALLBACK_KEY = 'persist:temp:sharedAccountPaymentMethod',
  DASHBOARD_CURRENCY_FILTER = 'persist:dashboardCurrencyFilter',
  OFFERING_RATE_FORM_SORT_VALUE = 'persist:offeringRateFormSortValue',
  CHAT_PANEL_WIDTH = 'persist:chatPanelWidth',
  /** Survives logout so the next login can go straight back into this tenant */
  LAST_TENANT_ID = 'persist:lastTenantId',
  /** Survives logout so the account Contracts tab keeps the user's Cards/Table pick */
  CONTRACTS_VIEW_MODE = 'persist:contractsViewMode',
}

/**
 * Used for filters and Pagers
 * This is the key used to know what entry in localstorage to use
 */
export enum FilterStateKeys {
  PRODUCT_LIST = 'PRODUCT_LIST',
  OFFERING_LIST = 'OFFERING_LIST',
  DISCOUNT_LIST = 'DISCOUNT_LIST',
  USAGE_TYPE_LIST = 'USAGE_TYPE_LIST',
  ACCOUNT_LIST = 'ACCOUNT_LIST',
  QUOTE_LIST = 'QUOTE_LIST',
  QUOTE_DASHBOARD_LIST = 'QUOTE_DASHBOARD_LIST',
  CONTRACT_LIST = 'CONTRACT_LIST',
  CONTRACT_DASHBOARD_LIST = 'CONTRACT_DASHBOARD_LIST',
  OPPORTUNITY_LIST = 'OPPORTUNITY_LIST',
  OPPORTUNITY_DASHBOARD_LIST = 'OPPORTUNITY_DASHBOARD_LIST',
  INVOICE_LIST = 'INVOICE_LIST',
  DASHBOARD_APPROVAL_LIST = 'DASHBOARD_APPROVAL_LIST',
  USER_LIST = 'USER_LIST',
  APPROVAL_RULES_LIST = 'APPROVAL_RULES_LIST',
  VALIDATION_RULES_LIST = 'VALIDATION_RULES_LIST',
  CONDITIONAL_TERM_RULES_LIST = 'CONDITIONAL_TERM_RULES_LIST',
  RECORD_AUTOMATION_RULES_LIST = 'RECORD_AUTOMATION_RULES_LIST',
  QUOTE_SHARE_RULES_LIST = 'QUOTE_SHARE_RULES_LIST',
  CUSTOM_FIELDS_LIST = 'CUSTOM_FIELDS_LIST',
  GUIDED_QUOTING_LIST = 'GUIDED_QUOTING_LIST',
  REFERENCE_RESOURCE_LIST = 'REFERENCE_RESOURCE_LIST',
  BUCKET_LIST = 'BUCKET_LIST',
}
const FILTER_STATE_KEY_SPLIT_CHAR = '$$';

export const combineFilterStateKey = (key: FilterStateKeys, options?: any) =>
  `${key}${FILTER_STATE_KEY_SPLIT_CHAR}${JSON.stringify(options)}`;

export const TableFilterSchema = z.record(z.string(), z.array(z.any()));
export const TablePagerSchema = z.record(z.string(), DataTablePagerSchema);
export const TableSearchTermSchema = z.record(z.string(), z.string());
export type IFilterStorageState = PartialRecord<FilterStateKeys, FilterType[]>;

export const stringValueSchema = z.string();
export const numberValueSchema = z.number();
export const arrayValueSchema = z.array(z.any());

export const SortSchema = z.object({
  value: z.string(),
  order: z.enum(['asc', 'desc']).default('asc'),
});
export type SortType = z.infer<typeof SortSchema>;

export const ContractsViewModeSchema = z.enum(['cards', 'table']);
export type ContractsViewMode = z.infer<typeof ContractsViewModeSchema>;

export const TenantStorageSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  isAdmin: z.boolean().default(false),
  status: z
    .nativeEnum(TenantStatusEnum)
    .optional()
    .default(TenantStatusEnum.ACTIVE),
  testTenant: z.boolean().default(true),
  timezone: z.string().default('UTC'),
});

export const quoteLocalPreferencesSchema = z.record(
  z.string(),
  z.object({ advanced: z.boolean() }),
);
export type QuoteLocalPreferences = z.infer<typeof quoteLocalPreferencesSchema>;

export const TempDocusignInfoSchema = z.object({
  data: DocusignConfigSchema,
  method: z.enum(['post', 'put']),
});
export type TempDocusignInfo = z.infer<typeof TempDocusignInfoSchema>;

export const RedirectInfoSchema = z.object({
  tenantId: z.string().nullish(),
  redirectUrl: z.string().nullish(),
});
export type RedirectInfo = z.infer<typeof RedirectInfoSchema>;

export const SharedQuotePaymentMethodStripeCallbackSchema = z.object({
  recordId: z.string(),
  tenantId: z.string(),
  token: z.string(),
  stripePublicKey: z.string(),
  paymentGatewayId: z.string(),
});
export type SharedQuotePaymentMethodStripeCallbackTemp = z.infer<
  typeof SharedQuotePaymentMethodStripeCallbackSchema
>;

export const SharedAccountPaymentMethodStripeCallbackSchema = z.object({
  accountId: z.string(),
  tenantId: z.string(),
  token: z.string(),
  stripePublicKey: z.string(),
  paymentGatewayId: z.string(),
  billGroupId: z.string().optional(),
});
export type SharedAccountPaymentMethodStripeCallbackTemp = z.infer<
  typeof SharedAccountPaymentMethodStripeCallbackSchema
>;

export function getDefaultFilter(key: string): FilterType[] {
  const isAccountScoped = key.includes(FILTER_STATE_KEY_SPLIT_CHAR);
  const testAccountDefault = isAccountScoped
    ? []
    : [hideTestAccountDefaultFilter()];

  switch (key.split(FILTER_STATE_KEY_SPLIT_CHAR)[0]) {
    case FilterStateKeys.DISCOUNT_LIST:
      return [
        {
          key: 'accountId',
          value: false,
          operator: FilterTypeOperator.TOGGLE,
          options: {
            falseValue: 'eq:null',
          },
        },
        {
          key: 'status',
          value: [DiscountStatusEnum.ACTIVE],
          operator: FilterTypeOperator.IN,
        },
      ];
    case FilterStateKeys.OFFERING_LIST:
      return [
        {
          key: 'status',
          value: [OfferingStatusEnum.ACTIVE],
          operator: FilterTypeOperator.IN,
        },
      ];
    case FilterStateKeys.PRODUCT_LIST:
      return [
        {
          key: 'status',
          value: [ProductStatusEnum.ACTIVE],
          operator: FilterTypeOperator.IN,
        },
      ];
    case FilterStateKeys.USAGE_TYPE_LIST:
      return [
        {
          key: 'status',
          value: [UsageTypeStatusEnum.ACTIVE],
          operator: FilterTypeOperator.IN,
        },
      ];
    case FilterStateKeys.QUOTE_LIST:
      return [
        {
          key: 'collaborationAccess',
          operator: FilterTypeOperator.TOGGLE,
          options: {
            trueValue: 'in:VIEW,EDIT',
          },
          value: '',
        },
        ...testAccountDefault,
      ];
    case FilterStateKeys.INVOICE_LIST:
    case FilterStateKeys.CONTRACT_LIST:
      return testAccountDefault;
    case FilterStateKeys.ACCOUNT_LIST:
      return [
        {
          key: TEST_ACCOUNT_FILTER_KEY,
          operator: FilterTypeOperator.EQUAL,
          value: HIDE_TEST_ACCOUNT_TRUE_VALUE,
        },
      ];
    case FilterStateKeys.OPPORTUNITY_LIST:
    case FilterStateKeys.OPPORTUNITY_DASHBOARD_LIST:
    case FilterStateKeys.QUOTE_DASHBOARD_LIST:
    case FilterStateKeys.CONTRACT_DASHBOARD_LIST:
    case FilterStateKeys.APPROVAL_RULES_LIST:
    case FilterStateKeys.VALIDATION_RULES_LIST:
      return [];
    default:
      return [];
  }
}

export function getDefaultPager(key: string): TDataTablePager {
  switch (key.split(FILTER_STATE_KEY_SPLIT_CHAR)[0]) {
    case FilterStateKeys.OFFERING_LIST:
    case FilterStateKeys.DISCOUNT_LIST:
    case FilterStateKeys.APPROVAL_RULES_LIST:
    case FilterStateKeys.VALIDATION_RULES_LIST:
      return { ...DEFAULT_PAGER, sortField: 'name', sortOrder: 1 };
    case FilterStateKeys.CONTRACT_LIST:
      return { ...DEFAULT_PAGER, sortField: 'startDate', sortOrder: -1 };
    case FilterStateKeys.QUOTE_LIST:
    case FilterStateKeys.CONTRACT_DASHBOARD_LIST:
    case FilterStateKeys.OPPORTUNITY_LIST:
    case FilterStateKeys.OPPORTUNITY_DASHBOARD_LIST:
      return { ...DEFAULT_PAGER, sortField: 'modifyDate', sortOrder: -1 };
    case FilterStateKeys.PRODUCT_LIST:
    case FilterStateKeys.USAGE_TYPE_LIST:
    case FilterStateKeys.ACCOUNT_LIST:
    case FilterStateKeys.QUOTE_DASHBOARD_LIST:
    case FilterStateKeys.INVOICE_LIST:
      return { ...DEFAULT_PAGER };
    default:
      return { ...DEFAULT_PAGER };
  }
}

export enum StorageOptions {
  LOCAL = 'LOCAL',
  SESSION = 'SESSION',
  ALL = 'ALL',
}

export type StorageType =
  | StorageOptions.LOCAL
  | StorageOptions.SESSION
  | StorageOptions.ALL;

// Union of all schemas allowed in browser storage
export type SchemaValidators =
  | typeof arrayValueSchema
  | typeof ContractsViewModeSchema
  | typeof PaymentMethodReqSchemaUI
  | typeof quoteLocalPreferencesSchema
  | typeof RedirectInfoSchema
  | typeof SharedInvoicePaymentSchemaUI
  | typeof SharedQuotePaymentMethodStripeCallbackSchema
  | typeof SharedAccountPaymentMethodStripeCallbackSchema
  | typeof numberValueSchema
  | typeof SortSchema
  | typeof stringValueSchema
  | typeof TableFilterSchema
  | typeof TablePagerSchema
  | typeof TableSearchTermSchema
  | typeof TempDocusignInfoSchema
  | typeof TenantStorageSchema;

interface IBrowserStorageProps<T> {
  persistKey: PersistKeysEnum;
  schemaValidator: T;
}

export interface IGetBrowserStorageProps<
  T extends SchemaValidators,
  TDefault,
> extends IBrowserStorageProps<T> {
  defaultValue?: z.infer<T> | TDefault;
  type?: StorageType;
}

export interface ISaveToBrowserStorageProps<
  T extends SchemaValidators,
> extends IBrowserStorageProps<T> {
  value: z.infer<T>;
  type?: StorageType;
  transformFn?: (value: z.infer<T>) => z.infer<T>;
}
